import { Alert, Box, Collapse, FormControlLabel, Switch, TextField } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { BRAND_CORAL } from '../../constants'
import { VendorOnboardingForm } from '../../interfaces'
import {
    digitsOnly,
    normaliseAlnum,
    stateFromGstin,
    validateGstin,
    validatePincode,
} from '../../validators'
import PhoneField from '../PhoneField'
import { FieldGrid, SectionLabel, StageHeading, textFieldSx } from '../StepChrome'

const AddressFields = ({ prefix }: { prefix: 'billing' | 'shipping' }) => {
    const { control } = useFormContext<VendorOnboardingForm>()
    const required = prefix === 'billing'

    return (
        <FieldGrid>
            <Controller
                control={control}
                name={`${prefix}.attention` as const}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="Attention"
                        fullWidth
                        sx={{ ...textFieldSx, gridColumn: { sm: '1 / -1' } }}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.street1` as const}
                rules={required ? { required: 'Street address is required' } : {}}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label={`Street 1${required ? ' *' : ''}`}
                        fullWidth
                        sx={{ ...textFieldSx, gridColumn: { sm: '1 / -1' } }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.street2` as const}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="Street 2"
                        fullWidth
                        sx={{ ...textFieldSx, gridColumn: { sm: '1 / -1' } }}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.city` as const}
                rules={required ? { required: 'City is required' } : {}}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label={`City${required ? ' *' : ''}`}
                        fullWidth
                        sx={textFieldSx}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.state` as const}
                rules={required ? { required: 'State is required' } : {}}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label={`State${required ? ' *' : ''}`}
                        fullWidth
                        sx={textFieldSx}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.pinCode` as const}
                rules={required ? { validate: validatePincode } : {}}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        onChange={(event) =>
                            field.onChange(digitsOnly(event.target.value, 6))
                        }
                        label={`PIN code${required ? ' *' : ''}`}
                        fullWidth
                        sx={textFieldSx}
                        inputMode="numeric"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? '6 digits'}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefix}.country` as const}
                rules={required ? { required: 'Country is required' } : {}}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label={`Country/Region${required ? ' *' : ''}`}
                        fullWidth
                        sx={textFieldSx}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                    />
                )}
            />
            <PhoneField
                name={`${prefix}.phone`}
                label="Phone"
                required={required}
                sx={{ gridColumn: { sm: '1 / -1' } }}
            />
        </FieldGrid>
    )
}

const InvoicingStep = () => {
    const { control, watch, setValue, getValues } =
        useFormContext<VendorOnboardingForm>()

    const gstNumber = watch('gstNumber')
    const sameAsBilling = watch('sameAsBilling')
    const autoFilledFor = useRef<string | null>(null)

    // Derive Billing State from the GSTIN state code, but only ever fill a blank
    // field or one we filled ourselves — never clobber what the vendor typed.
    useEffect(() => {
        if (!gstNumber || gstNumber.length < 2) return
        const derived = stateFromGstin(gstNumber)
        if (!derived) return

        const current = getValues('billing.state')
        if (!current || current === autoFilledFor.current) {
            setValue('billing.state', derived, { shouldValidate: false })
            autoFilledFor.current = derived
        }
    }, [gstNumber, getValues, setValue])

    const derivedState = gstNumber ? stateFromGstin(gstNumber) : null
    const billingState = watch('billing.state')
    const stateMismatch =
        derivedState && billingState && derivedState !== billingState

    return (
        <Box>
            <StageHeading
                title="Invoicing"
                blurb="Where invoices should be raised and delivered."
            />

            <Controller
                control={control}
                name="gstNumber"
                rules={{
                    validate: (value) => validateGstin(value, getValues('panNumber')),
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        onChange={(event) =>
                            field.onChange(normaliseAlnum(event.target.value, 15))
                        }
                        label="GST number *"
                        fullWidth
                        sx={textFieldSx}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                        error={Boolean(fieldState.error)}
                        helperText={
                            fieldState.error?.message ??
                            '15 characters, e.g. 27ABCDE1234F1Z5'
                        }
                    />
                )}
            />

            {stateMismatch && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Your GSTIN begins with a {derivedState} state code, but the billing
                    state says {billingState}. That can be legitimate — just worth a
                    second look.
                </Alert>
            )}

            <SectionLabel>Billing address</SectionLabel>
            <AddressFields prefix="billing" />

            <SectionLabel>Shipping address</SectionLabel>
            <Controller
                control={control}
                name="sameAsBilling"
                render={({ field }) => (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={field.value}
                                onChange={(event) => field.onChange(event.target.checked)}
                                sx={{
                                    '& .Mui-checked': { color: BRAND_CORAL },
                                    '& .Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: BRAND_CORAL,
                                    },
                                }}
                            />
                        }
                        label="Same as billing address"
                    />
                )}
            />

            <Collapse in={!sameAsBilling}>
                <Box sx={{ pt: 1 }}>
                    <AddressFields prefix="shipping" />
                </Box>
            </Collapse>
        </Box>
    )
}

export default InvoicingStep
